<template>
  <controller>
    <page-header title="Estrutura organizacional" />

    <main class="structure-page">
      <structure-editor v-if="editor" :type="editor.type" :item="editor.item" :catalog="catalog"
        @close="editor = null" @saved="handleSaved" />

      <section class="filter-panel" aria-labelledby="structure-filters-title">
        <div class="filter-heading">
          <div>
            <h2 id="structure-filters-title">Consultar estrutura</h2>
            <p>Localize e gerencie departamentos, áreas, subáreas, funções e responsáveis.</p>
          </div>
          <div class="filter-actions">
            <details v-if="canManage" class="add-menu">
              <summary class="btn btn-primary">Adicionar</summary>
              <div>
                <button type="button" @click="openCreate('department')">Departamento</button>
                <button type="button" @click="openCreate('sector')">Setor</button>
                <button type="button" @click="openCreate('function')">Função</button>
              </div>
            </details>
            <button type="button" class="btn btn-secondary" :disabled="loading || !hasFilters" @click="clearFilters">
              Limpar filtros
            </button>
          </div>
        </div>

        <div class="filters">
          <label class="search-field">
            <span>Busca</span>
            <input v-model.trim="filters.search" type="search" placeholder="Ex.: Financeiro ou 03.02" autocomplete="off">
          </label>
          <label>
            <span>Tipo</span>
            <select v-model="filters.type">
              <option value="all">Todos</option>
              <option value="department">Departamento</option>
              <option value="area">Área</option>
              <option value="subarea">Subárea</option>
              <option value="function">Função</option>
            </select>
          </label>
          <label v-if="canManage">
            <span>Status</span>
            <select v-model="filters.status" @change="view = 'list'">
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="all">Todos</option>
            </select>
          </label>
          <fieldset>
            <legend>Visualização</legend>
            <div class="view-switch">
              <button type="button" :class="{ active: view === 'tree' }" :aria-pressed="view === 'tree'"
                :disabled="filters.status !== 'active'" @click="view = 'tree'">Hierarquia</button>
              <button type="button" :class="{ active: view === 'list' }" :aria-pressed="view === 'list'" @click="view = 'list'">Lista</button>
            </div>
          </fieldset>
        </div>
      </section>

      <section class="summary" aria-label="Resumo da estrutura">
        <button type="button" :class="{ selected: summarySelection === 'department' }"
          :aria-pressed="summarySelection === 'department'" @click="applySummaryFilter('department')">
          <strong>{{ summary.departments }}</strong><span>Departamentos</span>
        </button>
        <button type="button" :class="{ selected: summarySelection === 'sector' }"
          :aria-pressed="summarySelection === 'sector'" @click="applySummaryFilter('sector')">
          <strong>{{ summary.areas }}</strong><span>Áreas e subáreas</span>
        </button>
        <button type="button" :class="{ selected: summarySelection === 'function' }"
          :aria-pressed="summarySelection === 'function'" @click="applySummaryFilter('function')">
          <strong>{{ summary.functions }}</strong><span>Funções</span>
        </button>
        <button type="button" :class="{ selected: summarySelection === 'people' }"
          :aria-pressed="summarySelection === 'people'" @click="applySummaryFilter('people')">
          <strong>{{ summary.activeCollaborators }}</strong><span>Pessoas ativas</span>
        </button>
      </section>

      <section v-if="loading" class="state-panel" aria-live="polite">
        <form-spinner aria-label="Carregando estrutura organizacional" />
        <p>Carregando estrutura organizacional...</p>
      </section>

      <section v-else-if="loadError" class="state-panel error" role="alert">
        <h2>Não foi possível carregar a estrutura</h2>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-secondary" @click="loadData">Tentar novamente</button>
      </section>

      <section v-else-if="!filteredNodes.length" class="state-panel">
        <h2>Nenhum item encontrado</h2>
        <p>Ajuste a busca ou limpe os filtros para consultar toda a estrutura.</p>
        <button v-if="hasFilters" type="button" class="btn btn-secondary" @click="clearFilters">Limpar filtros</button>
      </section>

      <section v-else class="results-panel" aria-labelledby="structure-results-title">
        <header class="results-heading">
          <div>
            <h2 id="structure-results-title">{{ showTree ? 'Hierarquia' : 'Resultado' }}</h2>
            <p aria-live="polite">{{ filteredNodes.length }} item(ns) disponível(is)</p>
          </div>
          <small v-if="generatedAt">Atualizado em {{ formatDate(generatedAt) }}</small>
        </header>

        <organization-tree v-if="showTree" :nodes="tree" />

        <div v-else class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Estrutura</th><th>Tipo</th><th>Localização</th><th>Pessoas ativas</th><th>Status</th>
                <th v-if="canManage"><span class="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="node in filteredNodes" :key="`${node.type}-${node.id}`">
                <td><strong>{{ node.name }}</strong><small>{{ node.code || 'Sem código' }}</small></td>
                <td>{{ typeLabel(node.type) }}</td>
                <td>{{ locationLabel(node) }}</td>
                <td>{{ activePeople(node) }}</td>
                <td><span class="status-badge" :class="node.status"><span aria-hidden="true" />{{ statusLabel(node.status) }}</span></td>
                <td v-if="canManage" class="actions-cell">
                  <button type="button" class="btn btn-secondary btn-small" @click="openEdit(node)">Editar</button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="mobile-list">
            <article v-for="node in filteredNodes" :key="`mobile-${node.type}-${node.id}`">
              <header>
                <div><strong>{{ node.name }}</strong><small>{{ node.code || 'Sem código' }}</small></div>
                <span class="status-badge" :class="node.status"><span aria-hidden="true" />{{ statusLabel(node.status) }}</span>
              </header>
              <dl>
                <div><dt>Tipo</dt><dd>{{ typeLabel(node.type) }}</dd></div>
                <div><dt>Localização</dt><dd>{{ locationLabel(node) }}</dd></div>
                <div><dt>Pessoas ativas</dt><dd>{{ activePeople(node) }}</dd></div>
              </dl>
              <button v-if="canManage" type="button" class="btn btn-secondary" @click="openEdit(node)">Editar</button>
            </article>
          </div>
        </div>
      </section>
    </main>
  </controller>
</template>

<script>
import UserService from '@/services/user.service.js'
import OrganizationalStructureService from '@/services/organizational-structure.service'
import OrganizationTree from '@/components/administrativo/estrutura/OrganizationTree.vue'
import StructureEditor from '@/components/administrativo/estrutura/StructureEditor.vue'

export default {
  name: 'EstruturaOrganizacional',
  components: { OrganizationTree, StructureEditor },
  data () {
    return {
      tree: [],
      catalog: { departments: [], sectors: [], functions: [] },
      loading: true,
      loadError: '',
      generatedAt: null,
      view: 'tree',
      editor: null,
      filters: { search: '', type: 'all', status: 'active', peopleOnly: false },
      summarySelection: null
    }
  },
  computed: {
    canManage () {
      return UserService.filterPermissions('gerenciarEstruturaOrganizacional')
    },
    flatNodes () {
      const flatten = nodes => nodes.flatMap(node => [node, ...flatten(node.children || [])])
      return flatten(this.tree)
    },
    catalogNodes () {
      if (!this.canManage || !this.catalog.departments.length) return this.flatNodes
      const departments = this.catalog.departments.map(item => ({ ...item, responsibleFunctions: [], counts: { activeCollaborators: 0 } }))
      const sectors = this.catalog.sectors.map(item => ({ ...item, type: item.parentId ? 'subarea' : 'area', responsibleFunctions: [], counts: { activeCollaborators: 0 } }))
      const functions = this.catalog.functions.map(item => ({ ...item, counts: { activeCollaborators: item.activeCollaborators } }))
      return [...departments, ...sectors, ...functions]
    },
    filteredNodes () {
      const term = this.normalize(this.filters.search)
      return this.catalogNodes.filter(node => {
        const matchesType = this.filters.type === 'all' || node.type === this.filters.type ||
          (this.filters.type === 'sector' && (node.type === 'area' || node.type === 'subarea'))
        const matchesStatus = !this.canManage || this.filters.status === 'all' || node.status === this.filters.status
        const matchesPeople = !this.filters.peopleOnly || (node.type === 'function' && this.activePeople(node) > 0)
        const searchable = this.normalize(`${node.name} ${node.code || ''} ${node.departmentName || ''} ${node.sectorName || ''}`)
        return matchesType && matchesStatus && matchesPeople && (!term || searchable.includes(term))
      })
    },
    hasFilters () {
      return Boolean(this.filters.search) || this.filters.type !== 'all' || this.filters.peopleOnly ||
        (this.canManage && this.filters.status !== 'active')
    },
    showTree () {
      return this.view === 'tree' && !this.hasFilters
    },
    summary () {
      return this.flatNodes.reduce((result, node) => {
        if (node.type === 'department') result.departments += 1
        if (node.type === 'area' || node.type === 'subarea') result.areas += 1
        if (node.type === 'function') {
          result.functions += 1
          result.activeCollaborators += node.counts?.activeCollaborators || 0
        }
        return result
      }, { departments: 0, areas: 0, functions: 0, activeCollaborators: 0 })
    }
  },
  mounted () { this.loadData() },
  methods: {
    async loadData () {
      this.loading = true
      this.loadError = ''
      try {
        const requests = [OrganizationalStructureService.tree()]
        if (this.canManage) requests.push(OrganizationalStructureService.catalog())
        const [treeResponse, catalogResponse] = await Promise.all(requests)
        this.tree = treeResponse.data.data || []
        this.generatedAt = treeResponse.data.meta?.generatedAt || null
        if (catalogResponse) this.catalog = catalogResponse.data.data
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Verifique sua conexão e tente novamente.'
      } finally { this.loading = false }
    },
    openCreate (type) {
      this.editor = { type, item: null }
      this.$nextTick(() => document.getElementById('structure-editor-title')?.focus())
    },
    async openEdit (node) {
      this.loading = true
      try {
        const response = await OrganizationalStructureService.show(this.apiType(node.type), node.id)
        this.editor = { type: this.apiType(node.type), item: response.data.data }
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
      } catch (error) {
        window.toaster.emit('open', { style: 'error', message: error.response?.data?.message || 'Não foi possível abrir o item.' })
      } finally { this.loading = false }
    },
    async handleSaved () {
      this.editor = null
      await this.loadData()
      this.view = 'list'
    },
    clearFilters () {
      this.filters = { search: '', type: 'all', status: 'active', peopleOnly: false }
      this.summarySelection = null
    },
    applySummaryFilter (selection) {
      if (this.summarySelection === selection) {
        this.clearFilters()
        return
      }
      this.summarySelection = selection
      this.filters.type = selection === 'people' ? 'function' : selection
      this.filters.status = 'active'
      this.filters.peopleOnly = selection === 'people'
      this.view = 'list'
    },
    normalize (value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() },
    apiType (type) { return type === 'area' || type === 'subarea' ? 'sector' : type },
    typeLabel (type) { return { department: 'Departamento', area: 'Área', subarea: 'Subárea', sector: 'Setor', function: 'Função' }[type] || type },
    locationLabel (node) { return node.sectorName || node.parentName || node.departmentName || 'Raiz da estrutura' },
    activePeople (node) { return node.activeCollaborators ?? node.counts?.activeCollaborators ?? 0 },
    statusLabel (status) { return status === 'inactive' ? 'Inativo' : 'Ativo' },
    formatDate (value) { return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) }
  }
}
</script>

<style lang="scss" scoped>
.structure-page { max-width: 1440px; margin: 0 auto; padding: $mg_mid; color: $color-dev; }
.filter-panel, .results-panel, .state-panel { margin-bottom: $mg_mid; padding: $hmg_mid; border: $border-component; border-radius: $border-radius; background: $color-secondary; }
.filter-heading, .results-heading { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; }
.filter-heading h2, .results-heading h2, .state-panel h2 { margin: 0 0 $hmg_mini; font-size: 18px; }
.filter-heading p, .results-heading p, .state-panel p, .results-heading small { margin: 0; color: $color-text; }
.filter-actions { display: flex; gap: $mg_small; align-items: center; }
.add-menu { position: relative; }
.add-menu summary { display: flex; align-items: center; min-height: 44px; cursor: pointer; list-style: none; }
.add-menu > div { position: absolute; right: 0; z-index: 5; min-width: 190px; margin-top: $hmg_mini; padding: $hmg_mini; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; }
.add-menu button { width: 100%; min-height: 44px; padding: 0 $mg_small; border: 0; background: transparent; text-align: left; cursor: pointer; }
.add-menu button:hover { background: $color-bg; }
.filters { display: grid; grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(170px, 1fr)); gap: $mg_small; margin-top: $hmg_mid; }
.filters label, .filters fieldset { display: flex; flex-direction: column; gap: $hmg_mini; margin: 0; padding: 0; border: 0; font-weight: 600; }
.filters legend { margin-bottom: $hmg_mini; font-size: $font-size; }
.filters input, .filters select { min-height: 44px; padding: 0 $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
.filters input:focus-visible, .filters select:focus-visible, button:focus-visible, summary:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.view-switch { display: grid; grid-template-columns: 1fr 1fr; min-height: 44px; border: $border-component; border-radius: $border-radius-mid; overflow: hidden; }
.view-switch button { border: 0; background: $color-secondary; color: $color-dev; font-weight: 600; cursor: pointer; }
.view-switch button.active { background: $color-primary; color: $color-secondary; }
.view-switch button:disabled { cursor: not-allowed; opacity: .5; }
.summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: $mg_small; margin-bottom: $mg_mid; }
.summary button { display: flex; align-items: baseline; gap: $mg_mini; min-height: 64px; padding: $hmg_small $hmg_mid; border: $border-component; border-radius: $border-radius; background: $color-secondary; color: $color-dev; text-align: left; cursor: pointer; }
.summary button:hover { border-color: $color-primary; }
.summary button.selected { border-color: $color-primary; box-shadow: inset 0 -3px 0 $color-primary; }
.summary strong { font-size: 24px; }.summary span { color: $color-text; }
.state-panel { text-align: center; padding: $mg; }.state-panel.error { border-color: $color-error !important; }
.results-heading { margin-bottom: $hmg_small; }.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; } th, td { padding: $mg_small; border-bottom: $border-component; text-align: left; vertical-align: middle; }
th { color: $color-text; font-size: $font-size-small; } td strong, td small { display: block; } td small { margin-top: $hmg_mini; color: $color-text; }
.status-badge { display: inline-flex; align-items: center; gap: $hmg_mini; min-height: 28px; padding: 0 $mg_mini; border-radius: $border-radius-xlarge; background: $color-active-light; font-size: $font-size-small; font-weight: 700; }
.status-badge > span { width: 8px; height: 8px; border-radius: 50%; background: $color-success-2; }.status-badge.inactive > span { background: $color-warning; }
.actions-cell { text-align: right; }.mobile-list { display: none; }.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
@media (max-width: 1024px) {
  .structure-page { padding: $hmg_mid; }.filters { grid-template-columns: 1fr 1fr; }.search-field { grid-column: 1 / -1; }.summary { grid-template-columns: 1fr 1fr; }
  .table-wrap > table { display: none; }.mobile-list { display: grid; gap: $mg_small; }.mobile-list article { padding: $hmg_small; border: $border-component; border-radius: $border-radius-mid; }
  .mobile-list header { display: flex; justify-content: space-between; gap: $mg_small; }.mobile-list header small { display: block; margin-top: $hmg_mini; color: $color-text; }
  .mobile-list dl { display: grid; gap: $mg_mini; margin: $mg_small 0; }.mobile-list dl div { display: grid; grid-template-columns: 120px 1fr; gap: $mg_mini; }.mobile-list dt { color: $color-text; }.mobile-list dd { margin: 0; }
}
@media (max-width: 640px) {
  .structure-page { padding: $mg_small; }.filter-heading, .results-heading { align-items: flex-start; flex-direction: column; }.filter-actions { width: 100%; flex-direction: column; }.filter-actions > * { width: 100%; }
  .add-menu summary { justify-content: center; }.add-menu > div { position: static; }.filters, .summary { grid-template-columns: 1fr; }.search-field { grid-column: auto; }.filter-heading .btn { width: 100%; }
}
@media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; } }
</style>
